import styled from "styled-components";

/* ---- 라이트/다크 토큰 (CSS 변수) ---- */
export const Section = styled.div`
  --bg: #ffffff;
  --bg-weak: #f8fafc;
  --text: #0f172a;
  --text-weak: #334155;
  --text-muted: #64748b;

  /* 윤곽선(컨투어) 계열 */
  --hairline: #e5e7eb; /* 바탕선 */
  --hairline-strong: #cbd5e1; /* 기본 테두리 */
  --hairline-accent: #94a3b8; /* 강조 테두리 */

  /* 포커스 링 */
  --ring: rgba(99, 102, 241, 0.28);

  /* 액션 강조색 */
  --acc-on: #22c55e; /* green-500 */
  --acc-off: #ef4444; /* red-500 */
  --acc-restart: #f59e0b; /* amber-500 */
  --acc-logoff: #64748b; /* slate-500 */

  /* 윤곽선 두께 (쉽게 조절하려면 여기만 수정) */
  --stroke-w: 1.5px;
  --stroke-w-hover: 2px;

  @media (prefers-color-scheme: ) {
    --bg: #0b1020;
    --bg-weak: #0f172a;
    --text: #e5e7eb;
    --text-weak: #cbd5e1;
    --text-muted: #94a3b8;

    --hairline: #1f2937;
    --hairline-strong: #324055;
    --hairline-accent: #475569;

    --ring: rgba(99, 102, 241, 0.36);

    /* 다크에선 강조색을 약간 더 밝게 */
    --acc-on: #34d399;
    --acc-off: #f87171;
    --acc-restart: #fbbf24;
    --acc-logoff: #94a3b8;
  }

  border-radius: 12px;
  /* 바깥 굵은 테두리 + 안쪽 얇은 헤어라인으로 윤곽 강조 */
  border: var(--stroke-w) solid var(--hairline-strong);
  box-shadow: inset 0 0 0 1px var(--hairline), 0 6px 14px rgba(15, 23, 42, 0.06);
  background: var(--bg);
  overflow: hidden;

  /* 좌측 밀착 + 래핑 */
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: 8px 10px;

  @media (max-width: 640px) {
    padding: 8px;
    gap: 6px 8px;
  }
`;

/* 툴바 래핑 컨테이너 */
export const Controls = styled.div`
  display: contents;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 220px;

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

export const Label = styled.label`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: var(--text-muted);
`;

/* 공통 인풋 베이스: 기본부터 윤곽선 선명 */
const baseInput = `
  height: 36px;
  padding: 0 10px;
  border-radius: 10px;

  /* 기본 외곽선 + 안쪽 헤어라인: 이중 스트로크 */
  border: var(--stroke-w) solid var(--hairline-strong);
  box-shadow: inset 0 0 0 1px var(--hairline);

  background: var(--bg);
  color: var(--text);
  font-size: 13px;
  font-weight: 800;
  outline: none;

  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    background-color 0.12s ease,
    transform 0.08s ease;

  &::placeholder {
    color: var(--text-muted);
    opacity: 0.9;
    font-weight: 600;
  }

  &:hover {
    border-color: var(--hairline-accent);
    box-shadow:
      inset 0 0 0 1.5px var(--hairline-accent),
      0 1px 0 rgba(15,23,42,0.06);
    transform: translateY(-0.5px);
  }

  &:focus-visible {
    border-color: var(--hairline-accent);
    box-shadow:
      inset 0 0 0 var(--stroke-w-hover) var(--hairline-accent),
      0 0 0 4px var(--ring);
  }
`;

export const Select = styled.select`
  ${baseInput}
  appearance: none;
  padding-right: 30px;
  background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
`;

export const Input = styled.input`
  ${baseInput}

  &::-webkit-calendar-picker-indicator {
    opacity: 0.9;
    cursor: pointer;
  }
`;

/* 👉 왼쪽 정렬 유지 */
export const Presets = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

/* 프리셋 버튼도 윤곽 강화 */
export const GhostButton = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;

  border: var(--stroke-w) solid var(--hairline-strong);
  box-shadow: inset 0 0 0 1px var(--hairline);

  background: var(--bg);
  color: var(--text-weak);
  font-weight: 900;
  font-size: 12px;
  cursor: pointer;

  transition: transform 0.1s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, color 0.12s ease, background 0.12s ease;

  &:hover {
    color: var(--text);
    background: var(--bg-weak);
    border-color: var(--hairline-accent);
    box-shadow: inset 0 0 0 var(--stroke-w-hover) var(--hairline-accent),
      0 8px 14px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: inset 0 0 0 var(--stroke-w-hover) var(--hairline-accent),
      0 0 0 4px var(--ring);
  }

  &:active {
    transform: translateY(0);
    box-shadow: inset 0 0 0 var(--stroke-w-hover) var(--hairline-accent);
  }
`;
